// ─── De dónde sale la foto de un producto ────────────────────────────────────
// Durante la migración hay fichas de las dos clases y las dos tienen que verse:
//
//   • `imagen_path` → la foto vive en el bucket (lo nuevo). La ficha pesa 40 bytes.
//   • `imagen`      → base64 dentro de la propia ficha (lo viejo, en retirada).
//
// El render de la aplicación es sincrónico y las URLs del bucket hay que pedirlas,
// así que aquí se guardan en un caché: `srcImagenProducto` responde al instante
// con lo que haya y, si falta, encarga la URL y vuelve a pintar cuando llega.
// Nadie más en la aplicación debería mirar `imagen` ni `imagen_path` directamente.
import { state } from "./state.js";
import { dataUrlDeStorage, urlsFirmadasProductos } from "./storage.js";

// ruta → { url, expira }  ·  las URLs firmadas caducan, así que se renuevan solas
const __urls = new Map();
// ruta → data-URL  ·  solo para el PDF, que necesita la foto embebida
const __embebidas = new Map();
const __pidiendo = new Set();
let __repintarPendiente = false;

const VIGENCIA_S = 60 * 60 * 24;          // 24 h: sobra para una jornada
const RENOVAR_ANTES_MS = 60 * 60 * 1000;  // se renueva con una hora de margen

function urlViva(path) {
  const e = __urls.get(path);
  return e && (e.expira - Date.now() > RENOVAR_ANTES_MS) ? e.url : null;
}

// Un solo repintado por ráfaga: al abrir Maestros se piden decenas de URLs y no
// tiene sentido volver a dibujar la pantalla por cada una que llega.
function repintarUnaVez() {
  if (__repintarPendiente) return;
  __repintarPendiente = true;
  setTimeout(async () => {
    __repintarPendiente = false;
    const { render } = await import("../ui/app.js");
    render();
  }, 120);
}

async function pedirUrls(paths) {
  const faltan = paths.filter(p => p && !urlViva(p) && !__pidiendo.has(p));
  if (!faltan.length) return;
  faltan.forEach(p => __pidiendo.add(p));
  try {
    const m = await urlsFirmadasProductos(faltan, VIGENCIA_S);
    const expira = Date.now() + VIGENCIA_S * 1000;
    m.forEach((url, path) => __urls.set(path, { url, expira }));
    if (m.size) repintarUnaVez();
  } catch (err) {
    console.error("⚠️ No se pudieron firmar las fotos de producto:", err.message);
  } finally {
    faltan.forEach(p => __pidiendo.delete(p));
  }
}

// Lo que va en un <img src>. Sincrónico a propósito: devuelve "" mientras la URL
// no esté y deja encargada la petición, para que el render nunca se bloquee.
export function srcImagenProducto(prod) {
  if (!prod) return "";
  if (prod.imagen_path) {
    const url = urlViva(prod.imagen_path);
    if (url) return url;
    pedirUrls([prod.imagen_path]);
    // Si además conserva el base64 (ficha a medio migrar), se usa mientras llega.
    return prod.imagen || "";
  }
  return prod.imagen || "";
}

export function tieneImagenProducto(prod) {
  return !!(prod && (prod.imagen_path || prod.imagen));
}

// Se llama al terminar de cargar el catálogo: pide de una vez las URLs de todas
// las fichas, así la primera pantalla de Maestros ya sale con las fotos.
export function precargarImagenesProductos() {
  const paths = (state.data.productos || []).map(p => p.imagen_path).filter(Boolean);
  if (paths.length) pedirUrls(paths);
}

// ─── Para el PDF: las fotos embebidas ───────────────────────────────────────
// Devuelve un Map nombre-de-artículo → data-URL, ya descargado. Si una falla se
// omite: la cotización sale sin ESA foto, como cuando el producto no tiene, en
// vez de quedarse toda la generación colgada.
export async function embebidasParaDocumento(nombresArticulos) {
  const productos = state.data.productos || [];
  const buscados = [...new Set((nombresArticulos || []).filter(Boolean))];
  const salida = new Map();
  await Promise.all(buscados.map(async (nombre) => {
    const prod = productos.find(p => p.nombre === nombre);
    if (!prod) return;
    if (!prod.imagen_path) {
      if (prod.imagen) salida.set(nombre, prod.imagen);   // ficha sin migrar
      return;
    }
    if (__embebidas.has(prod.imagen_path)) {
      salida.set(nombre, __embebidas.get(prod.imagen_path));
      return;
    }
    try {
      const dataUrl = await dataUrlDeStorage(prod.imagen_path);
      __embebidas.set(prod.imagen_path, dataUrl);
      salida.set(nombre, dataUrl);
    } catch (err) {
      console.error(`⚠️ No se pudo descargar la foto de ${nombre}:`, err.message);
      if (prod.imagen) salida.set(nombre, prod.imagen);   // último recurso
    }
  }));
  return salida;
}

// La foto que la aplicación ya tiene en la mano se deja en el caché como si
// viniera del bucket. Hace falta justo después de subirla: la ficha se queda con
// `imagen_path` y sin base64, y si no se recuerda aquí la miniatura desaparece
// hasta la próxima carga (el modal es una cadena ya renderizada, así que un
// repintado no la regenera).
export function recordarImagen(path, dataUrl) {
  if (!path || !dataUrl) return;
  __urls.set(path, { url: dataUrl, expira: Date.now() + VIGENCIA_S * 1000 });
  __embebidas.set(path, dataUrl);
}

// Al cambiarle la foto a un producto, su copia en caché queda vieja.
export function olvidarImagenProducto(path) {
  if (!path) return;
  __urls.delete(path);
  __embebidas.delete(path);
}

// ─── Al guardar: la foto sube al bucket y la ficha se queda con la ruta ─────
// Recibe la ficha tal como la dejó el formulario (con `imagen` en base64) y la
// devuelve lista para guardar. Si la subida falla NO se pierde nada: se conserva
// el base64 y la ficha queda como antes, para reintentarlo en el próximo guardado.
export async function persistirImagenProducto(prod) {
  if (!prod || !prod.imagen || !/^data:image\//.test(prod.imagen)) return prod;
  try {
    const { subirImagenProducto } = await import("./storage.js");
    const path = await subirImagenProducto(prod.id, prod.imagen);
    olvidarImagenProducto(path);            // la anterior en caché ya no vale
    recordarImagen(path, prod.imagen);      // la nueva ya la tenemos: se ve sin pedirla
    return { ...prod, imagen_path: path, imagen: null };
  } catch (err) {
    console.error("⚠️ No se pudo subir la foto al bucket; queda en la ficha:", err.message);
    return prod;
  }
}
